// Spec §4 — Bonus duplicate-photo detection scoring (run client-side).
//
// In production a perceptual hash (pHash/dHash) of each bonus photo lives
// on the BonusPhotoSignature collection (see backend domain). We approximate
// that here by comparing the photo URL — exact match implies the same
// underlying image. Multiple corroborating signals (same machine, same
// amount, close timing, different customer) increase confidence.
//
// Returns: Map keyed by bonusId → { severity, confidence, reason, matchedWith[] }

import { DUPLICATE_FLAG_THRESHOLDS } from '../constants';

function toMinutes(timeStr) {
  if (!timeStr) return 0;
  const m = /(\d{1,2}):(\d{2})\s*(AM|PM)/i.exec(timeStr);
  if (!m) return 0;
  let h = parseInt(m[1], 10) % 12;
  if (m[3].toUpperCase() === 'PM') h += 12;
  // After-midnight AM rollover
  if (m[3].toUpperCase() === 'AM' && h < 12) h += 24;
  return h * 60 + parseInt(m[2], 10);
}

export function detectDuplicates(bonuses, signatureLookup = {}) {
  const flags = new Map();
  if (!Array.isArray(bonuses) || bonuses.length < 2) return flags;

  for (let i = 0; i < bonuses.length; i++) {
    for (let j = i + 1; j < bonuses.length; j++) {
      const a = bonuses[i];
      const b = bonuses[j];
      let score = 0;
      const signals = [];

      const sigA =
        signatureLookup[a.bonusId]?.signature || a.photo || a.imageUrl;
      const sigB =
        signatureLookup[b.bonusId]?.signature || b.photo || b.imageUrl;
      const photoMatch = sigA && sigB && sigA === sigB;
      if (photoMatch) {
        score += 40;
        signals.push('photo signature');
      }

      if (a.machine && a.machine === b.machine) {
        score += 20;
        signals.push('same machine');
      }

      const sameCustomer =
        a.customer === b.customer ||
        (a.firstName === b.firstName && a.lastName === b.lastName);

      if (
        Number(a.bonusAmount || a.amount) ===
          Number(b.bonusAmount || b.amount) &&
        (a.bonusName || a.specify) === (b.bonusName || b.specify)
      ) {
        score += 15;
        signals.push('identical bonus details');
      }

      const dt = Math.abs(toMinutes(a.time) - toMinutes(b.time));
      if (dt <= 5) {
        score += 25;
        signals.push('within 5 min');
      } else if (dt <= 15) {
        score += 10;
      }

      // Photo reused across DIFFERENT customers is much more suspicious
      // than the same customer hitting two similar bonuses.
      if (photoMatch && !sameCustomer) {
        score += 20;
        signals.push('different customers');
      }

      if (dt > 30) {
        score -= 15;
      }

      if (score >= DUPLICATE_FLAG_THRESHOLDS.MEDIUM) {
        const severity =
          score >= DUPLICATE_FLAG_THRESHOLDS.HIGH ? 'high' : 'medium';
        const reason =
          severity === 'high'
            ? `Strong match: ${signals.slice(0, 3).join(', ')}`
            : `Possible reuse: ${signals.slice(0, 2).join(', ')}`;
        const cappedConfidence = Math.min(100, score);

        // Keep the highest-severity flag and accumulate matched-with list.
        [
          [a.bonusId, b.bonusId],
          [b.bonusId, a.bonusId],
        ].forEach(([selfId, otherId]) => {
          if (!selfId) return;
          const existing = flags.get(selfId);
          if (!existing || existing.confidence < cappedConfidence) {
            flags.set(selfId, {
              severity,
              confidence: cappedConfidence,
              reason,
              matchedWith: [
                ...new Set([otherId, ...(existing?.matchedWith || [])]),
              ].filter(Boolean),
            });
          } else {
            existing.matchedWith = [
              ...new Set([...existing.matchedWith, otherId]),
            ].filter(Boolean);
          }
        });
      }
    }
  }

  return flags;
}
